import clsx from 'clsx';
import React, { FC, forwardRef, useContext, useMemo } from 'react';
import { useBlackcart, BlackcartContext } from '../../context';
import { Button } from '../Button';
import { IViolationMessage } from '@blackcart/base';

import styles from './EligibilityWarning.module.css';

type EligibilityWarningProps = {
  title?: string;
  onLearnMoreClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  colors?: {
    title?: string;
    message?: string;
    background?: string;
    button?: {
      outlined?: boolean;
      text?: string;
      background?: string;
    };
  };
  render?: (cartViolations: IViolationMessage) => JSX.Element;
} & Omit<React.HTMLProps<HTMLDivElement>, 'children'>;

const EligibilityWarning: FC<EligibilityWarningProps> = forwardRef<
  HTMLDivElement,
  EligibilityWarningProps
>((props, ref) => {
  const {
    title = 'Try before you buy',
    colors,
    render,
    id,
    className = '',
    style,
    onLearnMoreClick,
    ...rest
  } = props;
  const { cartViolations, bcCart } = useBlackcart();

  const { useModal, showBlackcartModal } = useContext(BlackcartContext);
  const bcViolations = useMemo(() => cartViolations(), [bcCart]);

  const handleLearnMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (useModal) {
      showBlackcartModal(event.currentTarget);
    }
    if (onLearnMoreClick) {
      onLearnMoreClick(event);
    }
  };

  if (!bcViolations) {
    return null;
  } else if (render) {
    return render(bcViolations);
  } else {
    return (
      <div
        id={id}
        className={clsx([
          styles.root,
          'bc-warning__root',
          className //
        ])}
        style={{
          ...(style || {}),
          '--bcWarning-titleColor': colors?.title,
          '--bcWarning-messageColor': colors?.message,
          '--bcWarning-bgColor': colors?.background
        }}
        ref={ref}
        {...rest}
      >
        <div className={clsx([styles.innerWrapper, 'bc-warning__inner-wrapper'])}>
          <div className={clsx([styles.title, 'bc-warning__title'])}>{title}</div>
          <p
            className={clsx([styles.message, 'bc-warning__message--main'])}
            dangerouslySetInnerHTML={{ __html: bcViolations.message }}
          />
          {bcViolations.details && (
            <p
              className={clsx([styles.message, 'bc-warning__message--details'])}
              dangerouslySetInnerHTML={{ __html: bcViolations.details }}
            />
          )}

          <Button
            className={clsx([styles.learnMore, 'bc-warning__button'])}
            text="Learn more"
            size="small"
            colors={{
              text: colors?.button?.text,
              background: colors?.button?.background
            }}
            outlined={colors?.button?.outlined}
            onClick={handleLearnMoreClick}
          />
        </div>
      </div>
    );
  }
});

export { EligibilityWarning };
